/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * MeetingSummary.tsx — Meetin\1 \2ummary (services/operator-web-app).
 */

import { useWorkspace } from '@/store';
import { Panel } from '@/components/ui';
import { getMeetingDraftSummary, formatMeetingSummaryLine } from './meetingDraftSummary';
import { useTranslation } from '@/i18n';

/**
 * E5-S3: Cross-widget consistency — single view of meeting (date + time + address).
 * Binds CalendarWidget and AddressWidget state; updates when either changes.
 */
export function MeetingSummary() {
  const t = useTranslation();
  const workspace = useWorkspace();
  const summary = getMeetingDraftSummary(workspace);
  const line = formatMeetingSummaryLine(summary, t('meeting.at'));

  return (
    <Panel title={t('meeting.place')} padding="md">
      <p
        className="text-sm font-medium"
        style={{ color: 'var(--text-secondary)' }}
        data-complete={summary.isComplete}
        aria-label={t('meeting.summary', { line })}
      >
        {line}
      </p>
      {!summary.isComplete && summary.missing.length > 0 && (
        <p className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
          {t('meeting.fill')}: {summary.missing.map((id) => fieldLabel(id, t)).join(', ')}
        </p>
      )}
      {summary.isComplete && (
        <p className="mt-2 text-xs text-green-600 dark:text-green-400">{t('meeting.complete')}</p>
      )}
    </Panel>
  );
}

function fieldLabel(fieldId: string, t: ReturnType<typeof useTranslation>): string {
  const labels: Record<string, string> = {
    date: t('meeting.date_accusative'),
    time: t('scenario.time').toLowerCase(),
    address: t('scenario.address').toLowerCase(),
  };
  return labels[fieldId] ?? fieldId;
}
